import '@/App.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth, setSessionExpiryHandler } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Landing } from '@/pages/Landing';
import { AdminLogin } from '@/pages/AdminLogin';
import axios from 'axios';
import { toast } from 'sonner';
import { Login } from '@/pages/Login';
import { Onboarding } from '@/pages/Onboarding';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminUsers } from '@/pages/admin/AdminUsers';
import { AdminPayments } from '@/pages/admin/AdminPayments';
import { AdminPaymentRecords } from '@/pages/admin/AdminPaymentRecords';
import { AdminReports } from '@/pages/admin/AdminReports';
import { AdminBulkImport } from '@/pages/admin/AdminBulkImport';
import { AdminSettings } from '@/pages/admin/AdminSettings';
import SupportPage from '@/pages/SupportPage';
import { AdminMessages } from '@/pages/admin/AdminMessages';
import { AdminSmsLogs } from '@/pages/admin/AdminSmsLogs';
import { DemoCheckout, DemoPaymentSuccess } from '@/pages/DemoPayment';
import { AdminAppointments } from '@/pages/admin/AdminAppointments';
import { AdminExpenses } from '@/pages/admin/AdminExpenses';
import { AdminAnnouncements } from '@/pages/admin/AdminAnnouncements';
import { AdminBranches } from '@/pages/admin/AdminBranches';
import { AdminPlanUpgrade } from '@/pages/admin/AdminPlanUpgrade';
import { BillingInfo } from '@/pages/admin/BillingInfo';
import { SuperAdminDashboard } from '@/pages/superadmin/SuperAdminDashboard';
import { UserDashboard } from '@/pages/user/UserDashboard';
import { UserPayments } from '@/pages/user/UserPayments';
import { UserNotifications } from '@/pages/user/UserNotifications';
import { UserProfile } from '@/pages/user/UserProfile';
import { UserAppointments } from '@/pages/user/UserAppointments';
import { UserExpenses } from '@/pages/user/UserExpenses';
import { UserAnnouncements } from '@/pages/user/UserAnnouncements';
import { Toaster } from '@/components/ui/sonner';
import { CookieConsent } from '@/components/CookieConsent';
import ResetPassword from '@/pages/ResetPassword';
import { Checkout } from '@/pages/Checkout';
import { PaddleCheckout } from '@/pages/PaddleCheckout';
import PublicBooking from '@/pages/public/PublicBooking';
import useNewBookingSound from '@/hooks/useNewBookingSound';

// Quote Builder (P1) — feature-flagged via REACT_APP_QUOTE_MODULE_ENABLED
const QUOTE_MODULE_ENABLED = process.env.REACT_APP_QUOTE_MODULE_ENABLED === 'true';
// Static requires: bundler will tree-shake them out when the flag is off
// because we only reference them below when the flag is truthy.
import { QuotesList } from '@/pages/quotes/QuotesList';
import { QuoteForm } from '@/pages/quotes/QuoteForm';
import { QuoteDetail } from '@/pages/quotes/QuoteDetail';
import { QuoteCustomers } from '@/pages/quotes/QuoteCustomers';
import { QuoteCatalog } from '@/pages/quotes/QuoteCatalog';
import { QuoteStock } from '@/pages/quotes/QuoteStock';
import { QuoteSettings } from '@/pages/quotes/QuoteSettings';
import { QuoteInsights } from '@/pages/quotes/QuoteInsights';
import { CompanySettings } from '@/pages/quotes/CompanySettings';
import { QuoteLogin } from '@/pages/quotes/QuoteLogin';
import { QuoteRegister } from '@/pages/quotes/QuoteRegister';
import { QuoteSetup } from '@/pages/quotes/QuoteSetup';
import { QuoteUpgrade } from '@/pages/quotes/QuoteUpgrade';
import { QuoteSetupGate } from '@/components/quotes/QuoteSetupGate';
import { QuotePermissionGate } from '@/components/quotes/QuotePermissionGate';
import { QuoteTeam } from '@/pages/quotes/QuoteTeam';
import { QuoteService } from '@/pages/quotes/QuoteService';

const FrozenScreen = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [subStatus, setSubStatus] = useState(null);
  const [payMonths, setPayMonths] = useState(1);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(null);

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Fetch subscription status for admins
  useEffect(() => {
    if (user?.role === 'admin') {
      axios.get(`${API_URL}/api/subscription/status`, { withCredentials: true })
        .then(({ data }) => setSubStatus(data))
        .catch(() => {});
    }
  }, [user, API_URL]);

  // Check for return from PayTR
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('sub_session_id');
    if (sessionId) {
      setPolling(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!polling) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/check/${polling}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          setPolling(null);
          window.location.reload();
          return;
        }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else setPolling(null);
      } catch { setPolling(null); }
    };
    poll();
  }, [polling, API_URL]);

  const handleSubscriptionPay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscription/pay`, {
        months: payMonths,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı');
      setPaying(false);
    }
  };

  const isContractExpired = subStatus?.frozen_reason === 'contract_expired';
  const isTrialExpired = user?.trial_status?.status === 'trial_expired';
  const monthlyFee = subStatus?.monthly_fee || 0;
  const totalAmount = monthlyFee * payMonths;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] p-4">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-8 text-center shadow-lg" data-testid="frozen-screen">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m9.364-7.364A9 9 0 1112 3a9 9 0 017.364 4.636z" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-red-700 mb-2">
          {isTrialExpired ? 'Demo Süreniz Sona Erdi' : isContractExpired ? 'Abonelik Süresi Doldu' : 'Hesap Donduruldu'}
        </h2>
        <p className="text-[#595959] mb-4">
          {isTrialExpired
            ? 'Sistemi kullanmaya devam etmek için lütfen bir paket satın alın.'
            : isContractExpired
              ? 'Sözleşme süreniz dolmuştur. Aboneliğinizi yenileyerek sisteme erişmeye devam edebilirsiniz.'
              : 'Hesabınız dondurulmuştur. Lütfen sistem yöneticisiyle iletişime geçin.'}
        </p>

        {/* Trial expired -> redirect to plan selection */}
        {isTrialExpired && user?.role === 'admin' && (
          <div className="mb-4">
            <a
              href="/admin/plan-upgrade"
              className="inline-flex items-center justify-center w-full px-6 py-3 bg-[#2563EB] text-white rounded-lg hover:bg-[#1E4ED8] transition font-semibold"
              data-testid="trial-expired-pay-btn"
            >
              Paket Seç & Ödeme Yap
            </a>
          </div>
        )}

        {/* Subscription payment for expired contracts */}
        {isContractExpired && monthlyFee > 0 && user?.role === 'admin' && (
          <div className="bg-[#F9F8F6] rounded-xl p-4 mb-4 text-left" data-testid="subscription-pay-section">
            <p className="text-sm font-medium text-[#1A1A2E] mb-3">Abonelik Yenileme</p>
            <div className="flex items-center gap-3 mb-3">
              <label className="text-sm text-[#595959]">Süre:</label>
              <select
                value={payMonths}
                onChange={(e) => setPayMonths(Number(e.target.value))}
                className="flex-1 rounded-lg border border-[#E5E5E5] px-3 py-2 text-sm"
                data-testid="sub-months-select"
              >
                {[1,2,3,6,12].map(m => (
                  <option key={m} value={m}>{m} Ay - {(monthlyFee * m).toLocaleString('tr-TR')} TL</option>
                ))}
              </select>
            </div>
            <p className="text-lg font-bold text-[#2563EB] mb-3 text-center">
              Toplam: {totalAmount.toLocaleString('tr-TR')} TL
            </p>
            <button
              onClick={handleSubscriptionPay}
              disabled={paying}
              className="w-full px-6 py-3 bg-[#2563EB] text-white rounded-lg hover:bg-[#1E4ED8] transition font-medium disabled:opacity-50"
              data-testid="pay-subscription-btn"
            >
              {paying ? 'İşleniyor...' : 'Online Ödeme Yap'}
            </button>
          </div>
        )}

        {polling && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-700 font-medium">Ödeme durumu kontrol ediliyor...</p>
          </div>
        )}

        <button onClick={handleLogout} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition" data-testid="frozen-logout-btn">Çıkış Yap</button>
      </div>
    </div>
  );
};

// Quote Builder aktifken admin/owner varsayılan iniş sayfası /quotes olur.
// Superadmin ve normal kullanıcılar hâlâ kendi panellerine gider.
const adminLandingPath = () => (QUOTE_MODULE_ENABLED ? '/quotes' : '/admin/dashboard');

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role === 'superadmin') {
    return <Navigate to="/superadmin" replace />;
  }

  if (user.role === 'admin' || user.role === 'staff') {
    return <Navigate to={adminLandingPath()} replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

const LandingOrRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  // If user is already logged in, redirect to dashboard
  if (user) {
    if (user.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    if (user.role === 'admin' || user.role === 'staff') {
      return <Navigate to={adminLandingPath()} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Anonymous root — Quote Builder aktifken sade teklif giriş ekranı,
  // yoksa eski Paydaai Landing (legacy).
  if (QUOTE_MODULE_ENABLED) return <QuoteLogin />;
  return <Landing />;
};

const LoginOrRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    if (user.role === 'admin' || user.role === 'staff') {
      return <Navigate to={adminLandingPath()} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to landing page which now has login form
  return <Navigate to="/" replace />;
};

// Watcher mounted once at app root — only polls when an admin/staff is logged in.
function AdminSoundWatcher() {
  const { user } = useAuth();
  useNewBookingSound({ enabled: user?.role === 'admin' || user?.role === 'staff' });
  return null;
}

/**
 * SessionExpiryBridge
 * -------------------
 * Bridges the axios 401 interceptor (which lives outside React) with the
 * router + i18n + toast system. Registered exactly once at app boot; the
 * interceptor triggers a single localized "session expired" toast, forces
 * logout state, and redirects the user to the correct login page based on
 * whether the Quote Builder module is enabled.
 */
function SessionExpiryBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('quotes');
  const { setUser } = useAuth();
  // Latest values in a ref so setSessionExpiryHandler's closure is fresh.
  const refs = useRef({ navigate, location, t, setUser });
  refs.current = { navigate, location, t, setUser };

  useEffect(() => {
    setSessionExpiryHandler(() => {
      try { refs.current.setUser?.(false); } catch { /* noop */ }
      const { pathname } = refs.current.location || {};
      const isLoginPage =
        pathname === '/'
        || pathname === '/login'
        || pathname === '/quote-login'
        || pathname === '/quote-register';
      // Avoid a redirect loop when the 401 came from the login form itself.
      if (!isLoginPage) {
        try {
          toast.error(refs.current.t('login.sessionExpired'));
        } catch { /* noop */ }
        try {
          const quoteEnabled = process.env.REACT_APP_QUOTE_MODULE_ENABLED === 'true';
          refs.current.navigate(quoteEnabled ? '/' : '/login', { replace: true });
        } catch { /* noop */ }
      }
    });
  }, []);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SessionExpiryBridge />
        <AdminSoundWatcher />
        <Routes>
          <Route path="/" element={<LandingOrRedirect />} />
          <Route path="/login" element={<LoginOrRedirect />} />
          {/* Eski kayıt sayfası (pazarlama + hizmet bildirimi tek kutuda) kaldırıldı. */}
          <Route path="/register" element={<Navigate to="/quote-register" replace />} />
          {/* Demo / marketing mockup routes — only active when REACT_APP_DEMO_MODE=true */}
          {process.env.REACT_APP_DEMO_MODE === 'true' && (
            <>
              <Route path="/demo/odeme" element={<DemoCheckout />} />
              <Route path="/demo/odeme-basarili" element={<DemoPaymentSuccess />} />
            </>
          )}
          <Route path={process.env.REACT_APP_ADMIN_PATH || '/admin-login'} element={<AdminLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/paddle-checkout" element={<PaddleCheckout />} />
          <Route path="/r/:slug" element={<PublicBooking />} />
          <Route path="/frozen" element={<FrozenScreen />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />

          <Route path="/dashboard-redirect" element={<RoleBasedRedirect />} />

          {/* Super Admin Route */}
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute superAdminOnly>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute adminOnly requiredFeature="payments">
                <AdminPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payment-records"
            element={
              <ProtectedRoute adminOnly requiredFeature="payments">
                <AdminPaymentRecords />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute adminOnly requiredFeature="reports">
                <AdminReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-import"
            element={
              <ProtectedRoute adminOnly requiredFeature="bulk_import">
                <AdminBulkImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/branches"
            element={
              <ProtectedRoute adminOnly>
                <AdminBranches />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plan-upgrade"
            element={
              <ProtectedRoute adminOnly allowWhenFrozen>
                <AdminPlanUpgrade />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute adminOnly>
                <BillingInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute adminOnly requiredFeature="messaging">
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sms-logs"
            element={
              <ProtectedRoute adminOnly>
                <AdminSmsLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedRoute adminOnly requiredFeature="appointments">
                <AdminAppointments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute adminOnly requiredFeature="expenses">
                <AdminExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/announcements"
            element={
              <ProtectedRoute adminOnly requiredFeature="announcements">
                <AdminAnnouncements />
              </ProtectedRoute>
            }
          />

          {/* User Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <UserPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <UserExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <UserNotifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <UserAnnouncements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-appointments"
            element={
              <ProtectedRoute>
                <UserAppointments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />

          {/* Quote Builder (P1) — routes registered only when the module is enabled. */}
          {QUOTE_MODULE_ENABLED && (
            <>
              {/* P1.2 — public register + post-register setup wizard */}
              <Route path="/quote-register" element={<QuoteRegister />} />
              <Route path="/quote-setup" element={<ProtectedRoute adminOnly><QuoteSetup /></ProtectedRoute>} />

              <Route path="/quotes" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quotes"><QuotesList /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quotes/new" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quotes"><QuoteForm /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quotes/:id" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quotes"><QuoteDetail /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quotes/:id/edit" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quotes"><QuoteForm /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-customers" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-customers"><QuoteCustomers /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-catalog" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-catalog"><QuoteCatalog /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-service" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-service"><QuoteService /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-team" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-team"><QuoteTeam /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-stock" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-stock"><QuoteStock /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-insights" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-insights"><QuoteInsights /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-settings" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-settings"><QuoteSettings /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/company-settings" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/company-settings"><CompanySettings /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
              <Route path="/quote-upgrade" element={<ProtectedRoute adminOnly><QuoteSetupGate><QuotePermissionGate page="/quote-upgrade"><QuoteUpgrade /></QuotePermissionGate></QuoteSetupGate></ProtectedRoute>} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      <CookieConsent />
    </AuthProvider>
  );
}

export default App;
